import React, { useEffect, useState } from 'react';
import leaseService from '../services/leaseService';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Alert from '../components/common/Alert';
import { formatDate } from '../utils/formatters';
import './ApplicantPages.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const LeaseCard = ({ lease, onReload, setNotice }) => {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const blob = await leaseService.downloadDocument(lease.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = lease.document_original_filename || 'lease.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This document could not be opened.') });
    }
  };

  const giveNotice = async () => {
    if (!window.confirm("Let your landlord know you don't intend to renew this lease?")) return;
    setBusy(true);
    try {
      await leaseService.giveNotice(lease.id);
      setNotice({ type: 'success', message: 'Your landlord has been told you are giving notice.' });
      onReload();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Notice could not be recorded.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="ap-card ap-list__item">
      <div>
        <h2 className="ap-list__title">{lease.property.address}</h2>
        <p className="ap-muted">
          {formatDate(lease.start_date)} — {formatDate(lease.end_date)} · R {lease.monthly_rent.toFixed(2)}/month
        </p>
        {lease.has_document && (
          <p><button type="button" className="btn-action" onClick={download}>Download {lease.document_original_filename}</button></p>
        )}
        {lease.notice_given_at && (
          <p className="ap-muted">Notice given by {lease.notice_given_by} on {formatDate(lease.notice_given_at)}.</p>
        )}
      </div>
      <div className="ap-list__actions">
        <StatusBadge status={lease.status} />
        {lease.status === 'active' && (
          <button type="button" className="btn-secondary ap-button" onClick={giveNotice} disabled={busy}>
            {busy ? 'Sending…' : 'Give notice'}
          </button>
        )}
      </div>
    </li>
  );
};

const MyLeasesPage = () => {
  const [leases, setLeases] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const load = () => {
    leaseService.mine().then(setLeases).catch((err) => setError(messageFrom(err, 'Your leases could not be loaded.')));
  };

  useEffect(() => { load(); }, []);

  if (error) return <Alert type="error" message={error} />;
  if (!leases) return <LoadingSpinner fullPage />;

  return (
    <div className="ap-page">
      <h1>My lease</h1>
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {leases.length === 0 ? (
        <div className="ap-card">
          <p>You have no lease on record yet. It will appear here once your landlord creates it.</p>
        </div>
      ) : (
        <ul className="ap-list">
          {leases.map((lease) => (
            <LeaseCard key={lease.id} lease={lease} onReload={load} setNotice={setNotice} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default MyLeasesPage;
